/* eslint-disable no-restricted-syntax */
import _ from 'lodash-es';
import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ITerminalData, ITerminalItem } from '../../types/Locations.type';
import {
  INominationHistory,
  INominationHistoryEntryExit,
} from '../../types/Nomination.type';
import { sendNotification } from '../../UI/Notification';
import { UK_TIME_ZONE } from '../../constants/Common';

dayjs.extend(utc);
dayjs.extend(timezone);

export const ERR_CODE_401 = 401;
export const ERR_CODE_403 = 403;
export const ERR_CODE_400 = 400;
export const ERR_CODE_500 = 500;

export const INTERVAL_TIME_60000 = 60000;
export const INTERVAL_TIME_120000 = 120000;

const ERROR_OCCURRED = 'An error occurred';

export interface IPaths {
  key: string;
  label: string;
  path?: string;
  children?: IPaths[];
}

export function getCurrentGasDay() {
  let currentGasDay = dayjs().tz(UK_TIME_ZONE);
  if (currentGasDay.hour() < 5) {
    currentGasDay = currentGasDay.subtract(1, 'day');
  }
  return currentGasDay;
}

export function getIsPastGasDay(gasDay: string) {
  const currentGasDay = getCurrentGasDay();
  return dayjs.tz(gasDay, UK_TIME_ZONE).isBefore(currentGasDay, 'day');
}

export function getIsWithinDay(gasDay: string) {
  const currentGasDay = getCurrentGasDay();
  return dayjs.tz(gasDay, UK_TIME_ZONE).isSame(currentGasDay, 'day');
}

export function getIsDayAhead(gasDay: string) {
  const currentGasDay = getCurrentGasDay();
  return dayjs
    .tz(gasDay, UK_TIME_ZONE)
    .isSame(currentGasDay.add(1, 'days'), 'day');
}

export function getAllPaths(array: IPaths[]) {
  const paths: string[] = [];

  function collectPaths(arr: IPaths[]) {
    for (const obj of arr) {
      if (obj.path) {
        paths.push(obj.path);
      }
      if (obj.children) {
        collectPaths(obj.children);
      }
    }
  }
  collectPaths(array);
  return paths;
}

export function extractFirstPaths(path: string): string {
  const nextSlashIndex = path.indexOf('/', 1);
  if (nextSlashIndex === -1) {
    return path;
  }
  return path.substring(0, nextSlashIndex);
}

export function findKeyByPath(
  array: IPaths[],
  path: string
): string | undefined {
  for (const obj of array) {
    if (obj.path?.includes(path)) {
      return obj.key;
    }
    if (obj.children) {
      const key = findKeyByPath(obj.children, path);
      if (key) {
        return key;
      }
    }
  }
  return undefined;
}

export function searchByText(data: unknown, searchText: string, properties) {
  // Helper function to check if any property contains the search text
  const containsText = (item, text, props) => {
    if (_.isEmpty(text)) {
      return true;
    } // If text is empty, consider all items as matching
    const lowerText = _.toLower(text);
    return _.some(props, (prop) => {
      return _.includes(_.toLower(item[prop] || ''), lowerText);
    });
  };

  // Recursive function to filter the nested data
  const filterNestedData = (items: unknown, text: string, props) => {
    return _.compact(
      _.map(items, (item) => {
        const matches = containsText(item, text, props);

        // Recursively filter subRows if they exist
        const subRows = item.subRows
          ? filterNestedData(item.subRows, text, props)
          : [];

        // Return the item if it matches or if it has matching subRows
        if (matches || !_.isEmpty(subRows)) {
          return { ...item, subRows };
        }

        return null;
      })
    );
  };

  return filterNestedData(data, searchText, properties);
}

export function transformData(response: ITerminalItem[]) {
  const result: ITerminalData[] = [];
  const terminalMap = new Map();

  response?.forEach((item) => {
    const {
      terminalId,
      terminalName,
      subTerminalId,
      subTerminalName,
      meterId,
      terminalIsActive,
      subTerminalIsActive,
      etrmExternalCode,
      isInterconnectionPoint,
      eicCode,
      adjacentTsoId,
      adjacentTsoName,
      geminiLocationCode,
      cvaCode,
    } = item;

    // Ensure terminal entry in the map
    if (!terminalMap.has(terminalId)) {
      terminalMap.set(terminalId, {
        id: terminalId.toString(),
        terminalName,
        subTerminalName: '',
        meterId: '',
        cvaCode: '',
        status: terminalIsActive ? 'Active' : 'Inactive',
        isActive: terminalIsActive,
        isInterconnectionPoint: null,
        geminiLocationCode,
        add: true,
        subRows: [],
        parentName: terminalName,
      });
    }

    const terminalEntry = terminalMap.get(terminalId);

    // Add sub-terminal to the correct terminal entry
    if (subTerminalId && subTerminalName !== null) {
      const subTerminalEntry = {
        id: `${terminalId}-${subTerminalId}`,
        terminalName: '',
        subTerminalName,
        meterId,
        cvaCode,
        status: subTerminalIsActive ? 'Active' : 'Inactive',
        isActive: subTerminalIsActive,
        add: false,
        parentName: terminalName,
        etrmExternalCode,
        isInterconnectionPoint,
        eicCode,
        adjacentTsoId,
        adjacentTsoName,
      };

      // Insert the subTerminalEntry in sorted order by subTerminalName
      const index = terminalEntry.subRows.findIndex(
        (subRow) =>
          subRow.subTerminalName.toLowerCase() > subTerminalName.toLowerCase()
      );
      if (index === -1) {
        terminalEntry.subRows.push(subTerminalEntry);
      } else {
        terminalEntry.subRows.splice(index, 0, subTerminalEntry);
      }
    }
  });

  // Convert map values to result array
  terminalMap?.forEach((value) => result.push(value));

  return result;
}

export const transformResponse = (noms: INominationHistory[]) => {
  return noms.map((item) => {
    // Destructure the item and prepare the top-level entry
    const { description, nominationSourceType, history } = item;

    // Create the top-level output item
    const mainItem = {
      id: history[0].id,
      description,
      nominationSourceType,
      start: `${history[0].start}:00`,
      nomination: history[0].nomination,
      change: history[0].change,
      changePercentage:
        Math.abs(parseFloat(String(history[0].changePercentage))) >= 0.01
          ? parseFloat(String(history[0].changePercentage)).toFixed(2)
          : 0,
      source: history[0].source,
      createdTimestamp: history[0].createdTimestamp,
      subRows: [] as INominationHistoryEntryExit[],
    };

    history.slice(1).forEach((entry) => {
      // Create the subRow entry
      const subRow: INominationHistoryEntryExit = {
        id: entry.id,
        description,
        nominationSourceType,
        start: `${entry.start}:00`,
        nomination: entry.nomination,
        change: entry.change,
        changePercentage:
          Math.abs(parseFloat(String(entry.changePercentage))) >= 0.01
            ? parseFloat(String(entry.changePercentage)).toFixed(2)
            : 0,
        source: entry.source,
        createdTimestamp: entry.createdTimestamp,
      };
      // Push subRow to the subRows array
      mainItem.subRows.push(subRow);
    });
    // Return the final output item
    return mainItem;
  });
};

export const formatToThousandSeparator = (value: number | Decimal) => {
  const parts = value.toString().split('.');
  const integerPart = parseInt(parts[0], 10).toLocaleString('en-US');
  const decimalPart = parts[1] ? `.${parts[1]}` : '';
  return integerPart + decimalPart;
};

export const renderRefreshMessage = (value: number | null) => {
  // empty message at initial stage
  if (value === null) {
    return null;
  }
  if (value === 0) {
    return 'Last refreshed less than 10 seconds ago';
  }
  return `Last refreshed ${value} minute${value > 1 ? 's' : ''} ago`;
};

export const sortByManualDeals = (tradingData) => {
  return tradingData.sort((a, b) => {
    if (a.dealType === 'Manual' && b.dealType !== 'Manual') {
      return -1;
    }
    if (a.dealType !== 'Manual' && b.dealType === 'Manual') {
      return 1;
    }
    return 0;
  });
};

export const formatTimeUnit = (value: number, unit: string) => {
  return `${value} ${unit}${value > 1 ? 's' : ''} ago`;
};

const DAYS = 30;
const MONTHS = 12;

export const formatDuration = (ms: number) => {
  if (ms < 1000) {
    return 'less than a second ago';
  }
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) {
    return formatTimeUnit(seconds, 'second');
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return formatTimeUnit(minutes, 'minute');
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return formatTimeUnit(hours, 'hour');
  }
  const days = Math.floor(hours / 24);
  if (days < DAYS) {
    return formatTimeUnit(days, 'day');
  }
  const months = Math.floor(days / DAYS);
  if (months < MONTHS) {
    return formatTimeUnit(months, 'month');
  }
  const years = Math.floor(months / MONTHS);
  return formatTimeUnit(years, 'year');
};

export const compareValues = <T>(
  valA: T,
  valB: T,
  direction: 'asc' | 'desc'
): number => {
  if (valA === null || valB === null) {
    return valA === null ? 1 : -1;
  }

  if (typeof valA === 'string' && typeof valB === 'string') {
    return direction === 'asc'
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA);
  }

  const valAValid = valA || valA === 0;
  const valBValid = valB || valB === 0;

  if (direction === 'asc') {
    return valAValid && valBValid && valA < valB ? -1 : 1;
  }
  return valAValid && valBValid && valA > valB ? -1 : 1;
};

export const handleError = (error, customMessage) => {
  if (!error?.message?.includes(ERROR_OCCURRED)) {
    sendNotification({
      type: 'error',
      message:
        typeof error?.response?.data === 'string'
          ? error?.response?.data
          : customMessage,
    });
  }
};

export const haveSameElements = (a, b) => {
  if (a.length !== b.length) {
    return false;
  }

  const counts = new Map();

  for (const num of a) {
    counts.set(num, (counts.get(num) ?? 0) + 1);
  }

  for (const num of b) {
    if (!counts.has(num)) {
      return false;
    }

    const next = counts.get(num) - 1;
    if (next === 0) {
      counts.delete(num);
    } else {
      counts.set(num, next);
    }
  }

  return counts.size === 0;
};
